/**
 * Shared types for Marketplace components
 */

import type {
  CustomMarketplace,
  CustomMarketplaceProductData,
  CustomColumn,
  CustomMarketplaceCountry,
  FulfillmentOption,
  CategoryColumnDefaults,
  PricingCalculatorImport
} from '../../types/database';
import type { Product } from '../../types';

export type ViewMode = 'grid' | 'table' | 'settings';
export type SettingsTab = 'general' | 'countries' | 'columns' | 'expenses' | 'amazonExpenses';

// Re-export for convenience
export type {
  CustomMarketplace,
  CustomMarketplaceProductData,
  CustomColumn,
  CustomMarketplaceCountry,
  FulfillmentOption,
  CategoryColumnDefaults,
  PricingCalculatorImport,
  Product,
};

// Default fulfillment options for Amazon
export const AMAZON_US_FULFILLMENT: FulfillmentOption[] = [
  { code: 'FBA', name: 'FBA', description: 'Fulfillment by Amazon', isDefault: true },
  { code: 'FBM-US', name: 'FBM (US)', description: 'Fulfilled by Merchant from US' },
  { code: 'FBM-TR', name: 'FBM (TR)', description: 'Fulfilled by Merchant from Turkey' },
];

export const AMAZON_DEFAULT_FULFILLMENT: FulfillmentOption[] = [
  { code: 'FBA', name: 'FBA', description: 'Fulfillment by Amazon', isDefault: true },
  { code: 'FBM', name: 'FBM', description: 'Fulfilled by Merchant' },
];

// Marketplace logo mapping
export const MARKETPLACE_LOGOS: Record<string, string> = {
  'AMZ': '/logos/amazon.svg',
  'N11': '/logos/n11.svg',
  'TY': '/logos/trendyol.svg',
  'HB': '/logos/hepsiburada.svg',
};

// Props interface for child components
export interface MarketplaceContextProps {
  // State
  selectedMarketplace: CustomMarketplace | null;
  selectedCountry: string;
  selectedFulfillment: string;
  products: Product[];
  exchangeRates: Record<string, number>;
  categoryDefaults: CategoryColumnDefaults[];

  // Setters
  setSelectedMarketplace: (mp: CustomMarketplace | null) => void;
  setSelectedCountry: (code: string) => void;
  setSelectedFulfillment: (code: string) => void;
  setViewMode: (mode: ViewMode) => void;
  setSettingsTab: (tab: SettingsTab) => void;

  // Handlers
  onMarketplaceUpdate: (marketplace: CustomMarketplace) => Promise<void>;
  onMarketplaceDelete: (id: string) => Promise<void>;
  onBackToGrid: () => void;
  onRefreshRates: () => Promise<void>;
  onReloadProducts: () => Promise<void>;

  // Computed
  fulfillmentOptions: FulfillmentOption[];
  selectedCountryData: CustomMarketplaceCountry | undefined;
  isLoadingRates: boolean;
}
